import React, { useState } from "react";
import { ApolloError, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import { useInvoiceData } from "hooks/useInvoiceData";
import useGetInvoiceStatusDetails from "hooks/useGetInvoiceStatusDetails";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Estimate01Icon,
  Mail01Icon,
  RecoveryMailIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Typography } from "@mui/material";
import RefundModal, { RefundRequest } from "./RefundModal";
import { GET_TRANSACTION_REFUNDABLE_AMOUNT } from "graphql/transaction";
import { ConfirmDialog } from "@outpave/ds";
import { formatCashFull } from "utils/formatCash";
import { getGraphqlErrorMessage } from "utils/graphql";

interface EditInvoiceConfirmationModalProps {
  openEditSentInvoiceModal: boolean;
  selectedInvoiceId: string | undefined;
  onCloseModal: () => void;
}

export const EditInvoiceConfirmationModal: React.FC<
  EditInvoiceConfirmationModalProps
> = ({ openEditSentInvoiceModal, selectedInvoiceId, onCloseModal }) => {
  const { handleOpenEditInvoicePage } = useInvoiceData({});
  const { t } = useTranslation();

  const handleConfirm = async () => {
    selectedInvoiceId && handleOpenEditInvoicePage(selectedInvoiceId);
    onCloseModal();
  };

  return (
    <ConfirmDialog
      open={openEditSentInvoiceModal}
      tone="primary"
      title={t("Edit Invoice Confirmation")}
      message={t("InvoiceEditWarningMessage")}
      confirmLabel={t("Yes")}
      cancelLabel={t("No")}
      onConfirm={handleConfirm}
      onCancel={onCloseModal}
    />
  );
};

interface SendInvoiceConfirmationModalProps {
  openSendInvoiceConfirmationModal: boolean;
  invoice: any;
  onCloseModal: (selectedYes?: boolean) => void;
}

export const SendInvoiceConfirmationModal: React.FC<
  SendInvoiceConfirmationModalProps
> = ({ openSendInvoiceConfirmationModal, invoice, onCloseModal }) => {
  const { handleSendInvoice, specificLoadingStates } = useInvoiceData({});
  const { isInvoiceSent } = useGetInvoiceStatusDetails(invoice?.status);
  const { t } = useTranslation();

  const handleConfirm = async () => {
    if (invoice.id) {
      await handleSendInvoice(invoice.id);
    }
    onCloseModal(true);
  };

  const sendInvoiceLabel = isInvoiceSent
    ? t("Resend Invoice")
    : t("Send Invoice");

  const email = invoice?.recipientEmail || invoice?.primaryRecipient?.email;

  const icon = isInvoiceSent ? (
    <HugeiconsIcon icon={RecoveryMailIcon} size={18} />
  ) : (
    <HugeiconsIcon icon={Mail01Icon} size={18} />
  );

  return (
    <ConfirmDialog
      open={openSendInvoiceConfirmationModal}
      tone="primary"
      title={sendInvoiceLabel}
      confirmLabel={t("Yes")}
      cancelLabel={t("No")}
      icon={icon}
      busy={specificLoadingStates.sendingInvoice}
      message={
        <>
          <Trans
            i18nKey="SendInvoiceDescription"
            components={{
              contact_email: (
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ color: "var(--color-text-brand)" }}
                />
              ),
            }}
            values={{ email: email }}
          />
        </>
      }
      onConfirm={handleConfirm}
      onCancel={() => {
        if (specificLoadingStates.sendingInvoice) return;
        onCloseModal();
      }}
    />
  );
};

interface ResendFailedPaymentNoticeModalProps {
  open: boolean;
  invoice: {
    id?: string;
    recipientEmail?: string;
    primaryRecipient?: { email?: string };
  };
  onCloseModal: (selectedYes?: boolean) => void;
}

export const ResendFailedPaymentNoticeModal: React.FC<
  ResendFailedPaymentNoticeModalProps
> = ({ open, invoice, onCloseModal }) => {
  const { handleResendFailedPaymentNotice, specificLoadingStates } =
    useInvoiceData({});
  const { t } = useTranslation();

  const handleConfirm = async () => {
    if (invoice.id) {
      await handleResendFailedPaymentNotice(invoice.id);
    }
    onCloseModal(true);
  };

  const email = invoice?.recipientEmail || invoice?.primaryRecipient?.email;

  return (
    <ConfirmDialog
      open={open}
      tone="primary"
      title={t("Re-message Customer")}
      confirmLabel={t("Yes")}
      cancelLabel={t("No")}
      icon={<HugeiconsIcon icon={RecoveryMailIcon} size={18} />}
      busy={specificLoadingStates.resendingFailedNotice}
      message={
        <Trans
          i18nKey="ResendFailedPaymentNoticeDescription"
          components={{
            contact_email: (
              <Typography
                component="span"
                variant="inherit"
                sx={{ color: "var(--color-text-brand)" }}
              />
            ),
          }}
          values={{ email: email }}
        />
      }
      onConfirm={handleConfirm}
      onCancel={() => {
        if (specificLoadingStates.resendingFailedNotice) return;
        onCloseModal();
      }}
    />
  );
};

interface SendEstimateConfirmationModalProps {
  openSendInvoiceAsEstimateModal: boolean;
  invoice: any;
  onCloseModal: (selectedYes?: boolean) => void;
}

export const SendInvoiceAsEstimateConfirmationModal: React.FC<
  SendEstimateConfirmationModalProps
> = ({ openSendInvoiceAsEstimateModal, invoice, onCloseModal }) => {
  const { handleSendInvoice, specificLoadingStates } = useInvoiceData({});
  const { t } = useTranslation();
  const { isEstimate } = useGetInvoiceStatusDetails(invoice?.status);

  const handleConfirm = async () => {
    if (invoice.id) {
      await handleSendInvoice(invoice.id, true);
    }
    onCloseModal(true);
  };

  const sendInvoiceLabel = isEstimate
    ? t("Resend Estimate")
    : t("Send Estimate");

  const email = invoice?.recipientEmail || invoice?.primaryRecipient?.email;

  return (
    <ConfirmDialog
      open={openSendInvoiceAsEstimateModal}
      tone="primary"
      title={sendInvoiceLabel}
      confirmLabel={t("Yes")}
      cancelLabel={t("No")}
      icon={<HugeiconsIcon icon={Estimate01Icon} size={18} />}
      busy={specificLoadingStates.sendingInvoice}
      message={
        <>
          <Trans
            i18nKey="SendEstimateDescription"
            components={{
              contact_email: (
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ color: "var(--color-text-brand)" }}
                />
              ),
            }}
            values={{ email: email }}
          />
        </>
      }
      onConfirm={handleConfirm}
      onCancel={() => {
        if (specificLoadingStates.sendingInvoice) return;
        onCloseModal();
      }}
    />
  );
};

interface RefundInvoiceConfirmationModalProps {
  openRefundInvoiceModal: boolean;
  invoice: any;
  onCloseModal: (isRefresh?: boolean) => void;
}

// The server's code for a refund matching one already on the payment, which allowDuplicate sends anyway.
const DUPLICATE_REFUND = "DUPLICATE_REFUND";

const isDuplicateRefund = (error: unknown) =>
  error instanceof ApolloError &&
  error.graphQLErrors.some((graphqlError) =>
    graphqlError.message.startsWith(DUPLICATE_REFUND),
  );

export const RefundInvoiceConfirmationModal: React.FC<
  RefundInvoiceConfirmationModalProps
> = ({ openRefundInvoiceModal, invoice, onCloseModal }) => {
  const { handleRefundInvoice, specificLoadingStates } = useInvoiceData({});
  const { t } = useTranslation();
  // A refund matching one already on the payment, held until the operator confirms it.
  const [repeatRefund, setRepeatRefund] = useState<RefundRequest | null>(null);

  const transactionId = invoice?.transaction?.id;
  const {
    data,
    loading,
    error: limitError,
  } = useQuery(GET_TRANSACTION_REFUNDABLE_AMOUNT, {
    variables: { id: transactionId },
    skip: !openRefundInvoiceModal || !transactionId,
    // The refund mutation answers only yes or no, so a cached limit may be one it already spent.
    fetchPolicy: "network-only",
    onError: () => toast.error(t("refundFeature.limitUnavailable")),
  });
  // Known only once this open's request answers: a reopen hands back the last answer while it refetches.
  const maxRefund =
    loading || limitError ? undefined : data?.transaction?.refundableAmount;

  const refund = async (request: RefundRequest, allowDuplicate = false) => {
    if (!invoice.id) return;
    try {
      await handleRefundInvoice({
        invoiceId: invoice.id,
        ...request,
        allowDuplicate,
      });
    } catch (error) {
      if (isDuplicateRefund(error) && !allowDuplicate) {
        setRepeatRefund(request);
        return;
      }
      setRepeatRefund(null);
      toast.error(
        // A duplicate that survives allowDuplicate is the same refund already under way.
        isDuplicateRefund(error)
          ? t("refundFeature.alreadyProcessing")
          : error instanceof ApolloError && error.graphQLErrors.length
            ? getGraphqlErrorMessage(error.graphQLErrors)
            : t("Please try again!"),
      );
      return;
    }
    setRepeatRefund(null);
    onCloseModal(true);
  };

  const repeatAmount = formatCashFull(
    repeatRefund?.amount ?? 0,
    invoice?.currency,
  );

  return (
    <>
      <RefundModal
        open={openRefundInvoiceModal}
        refundTitle={t("refundFeature.refundInvoiceConfirmation")}
        description={t("refundFeature.refundInvoice")}
        handleYes={(request) => refund(request)}
        onCloseModal={onCloseModal}
        isLoading={specificLoadingStates.refundingInvoice}
        maxRefund={maxRefund}
        invoice={invoice}
      />
      <ConfirmDialog
        open={!!repeatRefund}
        tone="primary"
        title={t("refundFeature.repeatRefundTitle", { amount: repeatAmount })}
        message={t("refundFeature.repeatRefundMessage", {
          amount: repeatAmount,
        })}
        confirmLabel={t("refundFeature.refundAgain")}
        cancelLabel={t("No")}
        busy={specificLoadingStates.refundingInvoice}
        onConfirm={() => repeatRefund && refund(repeatRefund, true)}
        onCancel={() => setRepeatRefund(null)}
      />
    </>
  );
};
